import { computed, onScopeDispose } from 'vue'

import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import type { SearchContext } from '@common/shared-ui-layer/types/search-context'

import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { Router } from 'vue-router'

const DEBOUNCE_MS = 300

export function useCoursesSearchContext(route: RouteLocationNormalizedLoaded, router: Router): SearchContext {
  const { CPNavigateTo } = useCPNavigation()

  const isCoursesPage = computed(() => route.path.includes('/courses/products/') || route.name?.toString().startsWith('courses-products'))
  const isCoursesLibraryPage = computed(
    () => route.path.includes('/courses/my-library') || route.name?.toString().startsWith('courses-my-library')
  )
  const isCoursesSearchPage = computed(() => route.name?.toString().startsWith('courses-search'))
  /** Any standalone `/courses` shell path (products, search, my-courses, library, etc.). */
  const isCoursesStandalonePath = computed(() => {
    const path = route.path?.toString() ?? ''
    return path === '/courses' || path.startsWith('/courses/')
  })

  const shouldShowSearch = computed(
    () => isCoursesStandalonePath.value || isCoursesPage.value || isCoursesLibraryPage.value || isCoursesSearchPage.value
  )

  const initialQuery = computed(() => (isCoursesSearchPage.value ? (route.query.q as string) || '' : ''))

  const onSubmit = (query: string) => {
    const trimmed = query?.trim()
    if (!trimmed) return
    void CPNavigateTo({ name: 'courses-search', query: { q: trimmed } }, true)
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  const onInputChange = (value: string) => {
    if (!shouldShowSearch.value) return
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      const trimmed = value?.trim()

      if (!trimmed) {
        if (isCoursesSearchPage.value) {
          router.replace({
            query: {},
          })
        }
        debounceTimer = null
        return
      }

      if (isCoursesSearchPage.value) {
        router.replace({
          query: { q: trimmed },
        })
      } else {
        void CPNavigateTo({ name: 'courses-search', query: { q: trimmed } }, true)
      }

      debounceTimer = null
    }, DEBOUNCE_MS)
  }

  onScopeDispose(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  })

  const onSearchOpen = () => {
    if (!isCoursesSearchPage.value) {
      void CPNavigateTo({ name: 'courses-search' }, true)
    }
  }

  const onSearchClose = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    const hasPreviousRoute = typeof window !== 'undefined' && window.history.state?.back
    if (hasPreviousRoute) {
      router.back()
    } else {
      void CPNavigateTo({ name: 'my-library' }, true)
    }
  }

  return {
    shouldShowSearch,
    initialQuery,
    onSubmit,
    onInputChange,
    onSearchOpen,
    onSearchClose,
  }
}
